import { useClientState } from 'state/useClientState';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useClientAmplify from './useClientAmplify';
import { useMiniappConfirm } from 'state/useMiniappConfirm';
import { getDB } from 'InitiateRxdb';
import { useTokenCredentials } from 'state/useTokenCredentials';
import { useAuthContext } from 'context/AuthContext';
import { ObjectId } from 'bson';
import { useGeneralContext } from 'context/GeneralContext';
import { insertMiniappStoreLogUtil } from 'hooks/useInsertMiniappStoreLog';

export default function useRxdbStoreLog() {
    const [from, setFrom] = useState('');
    const retryCounterRef = useRef(1);
    const cleanupSplashRef = useRef(null);
    const cleanupConfirmRef = useRef(null);
    const [clientAmplify] = useClientAmplify();

    const { getClient, updateClient } = useClientState((state) => ({
        getClient: state.getClient,
        updateClient: state.updateClient,
    }));

    const history = useHistory();
    const {
        setUserLocalStorage,
        setOrderCategoryLocalStorage,
    } = useGeneralContext();

    const {
        getOutletId,
        getSetting,
        getSalesType,
        getActiveSalesType,
        hitAPIEC2,
    } = useAuthContext();

    const {
        updateMiniappConfirm,
        getMiniappConfirmId,
        updateMiniappConfirmId,
    } = useMiniappConfirm((state) => ({
        updateMiniappConfirm: state.updateMiniappConfirm,
        getMiniappConfirmId: state.getMiniappConfirmId,
        updateMiniappConfirmId: state.updateMiniappConfirmId,
    }));

    const { getTokenCredentials } = useTokenCredentials((state) => ({
        getTokenCredentials: state.getTokenCredentials,
    }));

    const redirectUrl = () => {
        setTimeout(() => {
            if (getSetting() && getSetting().isDisplayLogin) {
                history.replace('/login');
            } else {
                setUserLocalStorage('guest');
                if (getSalesType().active === 1) {
                    let localType = getActiveSalesType();
                    delete localType.icon;
                    setOrderCategoryLocalStorage(JSON.stringify(localType));
                    history.replace('/menu-catalogue');
                } else {
                    history.replace('/order-category');
                }
            }
        }, 100);
    };

    /**
     * Subscribe to RxDB storelog changes for splash flow with retry mechanism.
     * Watches for documents with status 'miniapp_ok' and matching partition/id.
     * Retries up to 3 times on timeout (20s each) then redirects to /miniapp-failure.
     */
    const subscribe = async () => {
        const now = new Date();
        console.log('subscribe storelog splashscreen called at:', now.toLocaleTimeString());
        let querySub;
        let timeout;

        timeout = setTimeout(async () => {
            if (retryCounterRef.current < 3) {
                cleanup();
                retryCounterRef.current += 1;
                try {
                    const newId = new ObjectId().toString();
                    updateMiniappConfirmId(newId);
                    await insertMiniappStoreLogUtil(hitAPIEC2, getOutletId(), newId);
                } catch (error) {
                    console.error('Error inserting miniapp storelog on retry:', error);
                }
                subscribe().then((newCleanup) => {
                    cleanupSplashRef.current = newCleanup;
                });
            } else {
                cleanup();
                history.replace('/miniapp-failure');
            }
        }, 20000);

        const cleanup = () => {
            if (timeout) clearTimeout(timeout);
            if (querySub) querySub.unsubscribe();
        };

        const db = await getDB();
        if (!db || !db.storelog) {
            console.error('Database or storelog collection not ready');
            cleanup();
            return;
        }

        const obj = {
            status: 'miniapp_ok',
            partition: getOutletId(),
            id: getMiniappConfirmId(),
        };

        const query = db.storelog
            .findOne({ selector: obj })
            .sort({ _rev: 'desc' });

        querySub = await query.$.subscribe((doc) => {
            if (doc) {
                console.log('storelog splash ok received');
                cleanup();
                redirectUrl();
            }
        });

        return cleanup;
    };

    /**
     * Subscribe to RxDB storelog changes for confirm order flow with retry mechanism.
     * Watches for documents with status 'miniapp_ok' and matching partition/id.
     * Retries up to 3 times on timeout (5s each) then redirects to /miniapp-failure.
     */
    const subscribeConfirm = async () => {
        const now = new Date();
        console.log('subscribe storelog confirm called at:', now.toLocaleTimeString());
        let querySub;
        let timeout;

        timeout = setTimeout(async () => {
            if (retryCounterRef.current < 3) {
                cleanup();
                retryCounterRef.current += 1;
                try {
                    const newId = new ObjectId().toString();
                    updateMiniappConfirmId(newId);
                    await insertMiniappStoreLogUtil(hitAPIEC2, getOutletId(), newId);
                } catch (error) {
                    console.error('Error inserting miniapp storelog on retry:', error);
                }
                subscribeConfirm().then((newCleanup) => {
                    cleanupConfirmRef.current = newCleanup;
                });
            } else {
                cleanup();
                history.replace('/miniapp-failure');
            }
        }, 5000);

        const cleanup = () => {
            if (timeout) clearTimeout(timeout);
            if (querySub) querySub.unsubscribe();
        };

        const db = await getDB();
        if (!db || !db.storelog) {
            console.error('Database or storelog collection not ready');
            cleanup();
            return;
        }

        const obj = {
            status: 'miniapp_ok',
            partition: getOutletId(),
            id: getMiniappConfirmId(),
        };

        const query = db.storelog
            .findOne({ selector: obj })
            .sort({ _rev: 'desc' });

        querySub = await query.$.subscribe((doc) => {
            if (doc) {
                console.log('storelog confirm ok received');
                cleanup();
                updateMiniappConfirm(true);
            }
        });

        return cleanup;
    };

    useEffect(() => {
        const clients = getClient();
        const clientHasProperties = Object.keys(clients).length > 0;
        if (clientHasProperties && from === 'splash') {
            retryCounterRef.current = 1;
            subscribe().then((cleanup) => {
                cleanupSplashRef.current = cleanup;
            });
        }
        return () => {
            if (cleanupSplashRef.current) {
                cleanupSplashRef.current();
                cleanupSplashRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, [getClient(), from]);

    useEffect(() => {
        if (Object.keys(getClient()).length > 0 && from === 'confirm') {
            retryCounterRef.current = 1;
            subscribeConfirm().then((cleanup) => {
                cleanupConfirmRef.current = cleanup;
            });
        }
        return () => {
            if (cleanupConfirmRef.current) {
                cleanupConfirmRef.current();
                cleanupConfirmRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, [from]);

    useEffect(() => {
        if (!getClient().graphql && getTokenCredentials() && getTokenCredentials().access_token) {
            const client = clientAmplify(getTokenCredentials().access_token);
            updateClient(client);
        }
        // eslint-disable-next-line
    }, [getClient()]);

    return [() => setFrom('splash'), () => setFrom('confirm')];
}
